// src/auth/session.ts
// THE BEARER TOKEN, WHERE IT LIVES, AND HOW IT STAYS ALIVE FOR A TWO-HOUR SESSION.
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
// THREE WAYS A TOKEN GETS HERE, AND ONLY ONE OF THEM CAN RENEW ITSELF
// ══════════════════════════════════════════════════════════════════════════════════════════════
//   1. PASSWORD SIGN-IN (`signInWithSupabase`). Email + password to GoTrue's password grant. Comes
//      back with a refresh token, so the session renews itself for as long as the app is used. This
//      is the path a coach uses.
//   2. A PASTED TOKEN. A JWT from the Supabase dashboard or a curl. No refresh token exists for it,
//      so it dies at its `exp` and the app says so plainly instead of pretending otherwise. This is
//      the Founder's and a tester's path.
//   3. A JOIN LINK carrying `#token=…` in the fragment. Same as (2): no refresh token.
//
// The gateway verifies whatever arrives against Supabase (`supabase.auth.getUser(jwt)`); there is no
// login route on the gateway, and none is needed — Supabase Auth is the issuer, and this file talks
// to it directly. See src/auth/supabase.ts for why that is two `fetch` calls and not an SDK.
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
// WHY IndexedDB AND NOT localStorage
// ══════════════════════════════════════════════════════════════════════════════════════════════
// A service worker has no `localStorage` — the API does not exist in that scope. The token must be
// readable there for a background replay, so localStorage is disqualified outright, not merely
// discouraged. IndexedDB is also what the offline queue and the device id already use, so there is
// one store, one transaction model, and no chance of two halves of a session disagreeing.
//
// The token is sent to the configured API base and NOWHERE else (see api/client.ts), it is never
// placed in a URL query string, and nothing in this file or supabase.ts ever logs one.
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
// THE OFFLINE QUEUE AND THE ROTATING CREDENTIAL
// ══════════════════════════════════════════════════════════════════════════════════════════════
// A queued job stores its idempotency keys and its bytes. It does NOT store a token, and never has.
// Every request reads the CURRENT token at send time through `ensureFreshToken()`, so a credential
// that rotates underneath a two-hour upload changes nothing about the job: same
// `client_upload_uuid`, same parts already acknowledged, same row on the server. A job that captured
// a token at enqueue time would fail an hour later holding a credential that expired while it sat in
// a tunnel — which is the failure this note exists to keep out.

import { STORE, idbDelete, idbGet, idbPut } from '../store/idb';
import {
  isAuthFailure,
  missingSupabaseConfig,
  passwordGrant,
  refreshGrant,
  supabaseConfigured,
  type AuthFailure,
  type TokenSet,
} from './supabase';

/** The single kv row that holds the session. Exported so a test can age it without guessing. */
export const SESSION_STORAGE_KEY = 'session';
const SESSION_KEY = SESSION_STORAGE_KEY;

/**
 * Refresh this long before the token's own deadline.
 *
 * Two minutes, not two seconds. A `fetch` that starts at exp-2s and takes three seconds on a bar of
 * signal arrives expired; the whole point of refreshing proactively is to have finished before the
 * old token dies. It is also comfortably under Supabase's default one-hour lifetime, so a session
 * refreshes roughly once an hour rather than constantly.
 */
export const REFRESH_SKEW_MS = 120_000;

/** How the token in hand was obtained. Decides what the UI can honestly offer. */
export type SessionSource = 'password' | 'pasted' | 'join_link';

export interface Session {
  /** The raw Supabase access token (a JWT). */
  readonly token: string;
  /**
   * The refresh token, when there is one. NULL for a pasted token and for a join link — those
   * cannot be renewed, and the app says that out loud rather than discovering it mid-session.
   */
  readonly refreshToken: string | null;
  /** `sub`. Display only — the SERVER decides who you are. */
  readonly subject: string | null;
  /** When this access token stops being accepted, epoch ms. Null when it could not be established. */
  readonly expiresAt: number | null;
  readonly storedAt: number;
  readonly source: SessionSource;
  /** The email the session was created with, for the "signed in as" line. Display only. */
  readonly email: string | null;
}

/**
 * Read `sub` and `exp` out of a JWT payload WITHOUT verifying it.
 *
 * Display-only, and the distinction matters enough to be loud about: nothing in this client treats
 * the decoded contents as authority. The server re-verifies every request against Supabase, and RLS
 * decides row visibility at the database. If this returned a lie, the only consequence would be a
 * wrong name on a debug line — no screen would unlock.
 */
function peekJwt(token: string): { subject: string | null; expiresAt: number | null } {
  const parts = token.split('.');
  const payload = parts[1];
  if (parts.length !== 3 || !payload) return { subject: null, expiresAt: null };
  try {
    const b64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = JSON.parse(atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4))) as Record<string, unknown>;
    return {
      subject: typeof json['sub'] === 'string' ? json['sub'] : null,
      expiresAt: typeof json['exp'] === 'number' ? json['exp'] * 1000 : null,
    };
  } catch {
    // An opaque token is perfectly legal as far as this client is concerned — it just cannot be
    // previewed. Do not reject it here; let the SERVER be the one that says a token is no good.
    return { subject: null, expiresAt: null };
  }
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * AUTH EVENTS — so a screen can react to a session ending without polling for it
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

export type AuthEvent = 'signed_in' | 'signed_out' | 'refreshed';

const watchers = new Set<(e: AuthEvent) => void>();

export function onAuthChange(fn: (e: AuthEvent) => void): () => void {
  watchers.add(fn);
  return () => watchers.delete(fn);
}

function emit(e: AuthEvent): void {
  for (const fn of watchers) fn(e);
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * READ / WRITE
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

/**
 * Records written before refresh tokens existed have no `refreshToken` / `source` / `email`. They
 * are normalised on read rather than migrated, so an app that was installed yesterday keeps working
 * and its (unrefreshable) token behaves exactly as a pasted one — which is what it is.
 */
function normalise(raw: unknown): Session | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const r = raw as Partial<Session>;
  if (typeof r.token !== 'string' || !r.token) return undefined;
  return {
    token: r.token,
    refreshToken: typeof r.refreshToken === 'string' && r.refreshToken ? r.refreshToken : null,
    subject: typeof r.subject === 'string' ? r.subject : null,
    expiresAt: typeof r.expiresAt === 'number' ? r.expiresAt : null,
    storedAt: typeof r.storedAt === 'number' ? r.storedAt : 0,
    source: r.source === 'password' || r.source === 'join_link' ? r.source : 'pasted',
    email: typeof r.email === 'string' ? r.email : null,
  };
}

export async function loadSession(): Promise<Session | undefined> {
  return normalise(await idbGet<unknown>(STORE.kv, SESSION_KEY));
}

/** Store a token that arrived WITHOUT a refresh token: a paste, or a join link. */
export async function saveSession(token: string, source: SessionSource = 'pasted'): Promise<Session> {
  const trimmed = token.trim().replace(/^Bearer\s+/i, '');
  if (!trimmed) throw new Error('empty token');
  const { subject, expiresAt } = peekJwt(trimmed);
  const session: Session = {
    token: trimmed,
    refreshToken: null,
    subject,
    // For a pasted token the JWT's own `exp` is the only expiry information that exists. It is on
    // the server's clock, so it is advisory here — see `looksExpired`.
    expiresAt,
    storedAt: Date.now(),
    source,
    email: null,
  };
  await idbPut(STORE.kv, session, SESSION_KEY);
  emit('signed_in');
  return session;
}

/** Store a token set that came from a GoTrue grant. This is the only path with a refresh token. */
async function saveTokenSet(t: TokenSet, previous?: Session): Promise<Session> {
  const peeked = peekJwt(t.accessToken);
  const session: Session = {
    token: t.accessToken,
    // GoTrue rotates the refresh token on every use. Keeping the OLD one here — by spreading the
    // previous session over the new values, or by "only setting it if absent" — is how the second
    // refresh fails with `refresh_token_already_used` and logs a coach out an hour into a session.
    refreshToken: t.refreshToken,
    subject: t.userId ?? peeked.subject,
    expiresAt: t.expiresAt,
    storedAt: Date.now(),
    source: 'password',
    email: t.email ?? previous?.email ?? null,
  };
  await idbPut(STORE.kv, session, SESSION_KEY);
  return session;
}

export async function bearerToken(): Promise<string | null> {
  const s = await loadSession();
  return s?.token ?? null;
}

export function canRefresh(s: Session | undefined): boolean {
  return !!s?.refreshToken;
}

async function clearStoredSession(): Promise<void> {
  await idbDelete(STORE.kv, SESSION_KEY);
}

/** Sign out. Clears the stored session and tells the shell, so nothing keeps a stale screen up. */
export async function signOut(): Promise<void> {
  await clearStoredSession();
  emit('signed_out');
}

/** Kept for compatibility with existing callers. Same thing as `signOut`. */
export const clearSession = signOut;

/**
 * Is the stored token past its own deadline?
 *
 * ADVISORY. For a session that CAN refresh, this drives a proactive renewal. For one that cannot, it
 * drives a warning only — the request still goes out and the server still answers, because the
 * server is the authority and a clock-skewed phone deciding locally that a good token is dead would
 * lock a coach out mid-session for no reason.
 */
export function looksExpired(s: Session | undefined, now = Date.now()): boolean {
  return !!s?.expiresAt && s.expiresAt <= now;
}

/** Is it inside the proactive-refresh window (or already past it)? */
export function needsRefreshSoon(s: Session | undefined, now = Date.now()): boolean {
  return !!s?.expiresAt && s.expiresAt - now <= REFRESH_SKEW_MS;
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * SIGN IN
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

export type SignInResult = { readonly ok: true; readonly session: Session } | AuthFailure;

/**
 * Sign in with an email address and a password.
 *
 * ON FAILURE THIS STORES NOTHING. Not a partial session, not a placeholder, not a "we'll fix it on
 * the next request" marker — the previous session (if any) is left exactly as it was, and a failed
 * attempt cannot leave the app in a state where it believes it is signed in. Lane A made the old
 * stub THROW rather than return a fake session for this reason; the reason has not changed just
 * because the function now really works.
 *
 * The failure is returned as a VALUE, like every refusal in this app, so it survives being handed to
 * a screen intact instead of being caught by whatever `try` happens to be nearest and flattened into
 * "something went wrong".
 */
export async function signInWithSupabase(email: string, password: string): Promise<SignInResult> {
  if (!email.trim() || !password) {
    return {
      ok: false,
      code: 'invalid_credentials',
      message: 'Enter both an email address and a password.',
      status: 0,
      retryable: false,
    };
  }
  const r = await passwordGrant(email, password);
  if (isAuthFailure(r)) return r;
  const previous = await loadSession();
  const session = await saveTokenSet({ ...r.tokens, email: r.tokens.email ?? email.trim() }, previous);
  emit('signed_in');
  return { ok: true, session };
}

/** Can this build offer a password form at all? */
export { supabaseConfigured, missingSupabaseConfig };

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * REFRESH
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

export type RefreshOutcome =
  | { readonly ok: true; readonly session: Session; readonly refreshed: boolean }
  | {
    readonly ok: false;
    readonly reason: 'no_session' | 'not_refreshable' | 'rejected' | 'unreachable';
    readonly message: string;
    /** True when the session was cleared as a result. Only ever set for a DEFINITIVE rejection. */
    readonly signedOut: boolean;
  };

/**
 * Exactly one refresh may be in flight at a time.
 *
 * Supabase ROTATES the refresh token: using one invalidates it and issues another. Two concurrent
 * refreshes therefore race to spend the same single-use token, and the loser gets
 * `refresh_token_already_used` — which, if it were treated as "your session is dead", would log a
 * coach out precisely because the app was busy. And busy is exactly when it happens: the queue
 * drains a session job and an upload job within the same tick of an `online` event, both find an
 * expired token, and both try to fix it.
 *
 * So: one in-flight promise, and every other caller awaits the same one.
 */
let inFlight: Promise<RefreshOutcome> | null = null;

export function refreshInFlight(): boolean {
  return inFlight !== null;
}

/**
 * Renew the access token.
 *
 * @param staleToken - the token that just got a 401, if this is a reactive refresh. Passing it
 *   makes the "another context already rotated for us" check exact: if what is in IndexedDB is no
 *   longer the token that failed, someone else (another tab, the service worker, a concurrent
 *   request) has already renewed and the right move is to use theirs, not to spend a second
 *   rotation. Omit it for a proactive refresh, which uses the expiry window instead.
 */
export function refreshNow(opts: { staleToken?: string } = {}): Promise<RefreshOutcome> {
  if (inFlight) return inFlight;
  inFlight = doRefresh(opts).finally(() => { inFlight = null; });
  return inFlight;
}

async function doRefresh(opts: { staleToken?: string }): Promise<RefreshOutcome> {
  const current = await loadSession();
  if (!current) {
    return { ok: false, reason: 'no_session', message: 'You are not signed in.', signedOut: false };
  }

  // ── Someone else already did the work. Two shapes of "already", one for each caller.
  if (opts.staleToken !== undefined) {
    if (current.token !== opts.staleToken) {
      return { ok: true, session: current, refreshed: true };
    }
  } else if (current.expiresAt !== null && !needsRefreshSoon(current)) {
    return { ok: true, session: current, refreshed: false };
  }

  if (!current.refreshToken) {
    // A pasted token or a join link. There is nothing to renew WITH, and inventing something is not
    // available. Whether that means "signed out" depends on whether the token is actually dead:
    // an unexpired pasted token is perfectly usable and must not be thrown away.
    return {
      ok: false,
      reason: 'not_refreshable',
      message: 'This session was created from a pasted token, which cannot be renewed. When it '
        + 'expires you will need to paste a new one or sign in with an email and password.',
      signedOut: false,
    };
  }

  const r = await refreshGrant(current.refreshToken);

  if (r.ok) {
    const session = await saveTokenSet(r.tokens, current);
    emit('refreshed');
    return { ok: true, session, refreshed: true };
  }

  // ── THE FORK THAT MATTERS.
  //
  // A refresh that could not REACH Supabase says nothing about whether the credential is valid. A
  // coach who walked behind the pavilion must come back to a working session, not a login screen —
  // so a retryable failure leaves the stored session exactly where it is and reports the transport
  // problem. Only a definitive rejection (GoTrue looked at the token and said no) clears it.
  if (r.retryable) {
    return { ok: false, reason: 'unreachable', message: r.message, signedOut: false };
  }

  await clearStoredSession();
  emit('signed_out');
  return { ok: false, reason: 'rejected', message: r.message, signedOut: true };
}

/**
 * The token to put on the next request — renewed first if it is about to expire.
 *
 * THIS IS THE PROACTIVE HALF of the lifecycle, and it is why a two-hour filming session does not end
 * in a wall of 401s: every outbound request passes through here, so the renewal happens on whatever
 * the app was going to do next rather than needing a timer that a backgrounded phone will not run.
 *
 * It returns the token even when the refresh failed, and even when the token looks expired:
 *   · a refresh that could not reach the network leaves a token that may well still work (the
 *     deadline is this device's clock, which can be wrong);
 *   · the SERVER is the authority on whether a token is accepted, and a local refusal to send would
 *     be this client inventing a 401 the server never issued.
 * Returning null means there is genuinely no token stored at all.
 */
export async function ensureFreshToken(): Promise<string | null> {
  const s = await loadSession();
  if (!s) return null;
  // Nothing to anticipate: either the deadline is unknown, or it is comfortably in the future, or
  // there is no way to renew. In all three the stored token is what goes out.
  if (s.expiresAt === null || !needsRefreshSoon(s) || !s.refreshToken) return s.token;

  const out = await refreshNow();
  if (out.ok) return out.session.token;
  if (out.signedOut) return null;
  return s.token;
}

/**
 * Renew a session that is about to expire while the app sits in the foreground.
 *
 * `ensureFreshToken` covers every request, which covers a coach who is USING the app. This covers
 * the coach who has the Capture screen open and untouched for fifty minutes while a net session
 * runs — there is no request to hang the renewal on, so an interval checks. It is deliberately a
 * cheap poll of a stored value rather than a `setTimeout` to the exact deadline: a phone suspends
 * timers when the screen locks, and a timer that fired an hour late would fire once, having already
 * missed the window, whereas a poll notices on the next tick after the device wakes.
 */
export function startTokenRenewal(intervalMs = 60_000): () => void {
  if (typeof setInterval === 'undefined') return () => undefined;
  const tick = async (): Promise<void> => {
    const s = await loadSession();
    if (!s || !s.refreshToken || !needsRefreshSoon(s)) return;
    await refreshNow();
  };
  const id = setInterval(() => void tick(), intervalMs);
  // Also on wake: a phone that was asleep for an hour resumes with a dead token and, on iOS, may
  // not run the interval until something else happens first.
  const onVisible = (): void => { if (document.visibilityState === 'visible') void tick(); };
  if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisible);
  return () => {
    clearInterval(id);
    if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisible);
  };
}

/** Test seam: drop the in-flight refresh and every listener between suites. */
export function __resetAuthState(): void {
  inFlight = null;
  watchers.clear();
}
